import { useState, Fragment } from 'react'
import { graphql } from '@apollo/client/react/hoc'
import { useQuery } from '@apollo/client'
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom'
import { Popover, PopoverBody, Table, Badge, UncontrolledTooltip } from 'reactstrap'
import DatetimeRenderer from "../Material/DatetimeRenderer"
import TaskDetailsSidePanel from './TaskDetailsSidePanel'
import PrimaryListingContainer from "../Material/PrimaryListingContainer"
import { AllTasksQuery } from "./Queries"
import toTitleCase from "../Functions/toTitleCase"
import { HasPermissionsQuery } from "../Functions/querys"
import EditTaskPopOver from "./TaskComponents/EditTaskPopOver"
import withApolloClient from '../withApolloClient'
import RepAvatar from "../Material/RepAvatar"
import RenderAddCustomColumn from "./TaskComponents/RenderAddCustomColumn"
import RenderEditColumnPopover from './TaskComponents/RenderEditColumnPopover'
import TaskArchievePopover from './TaskComponents/TaskArchievePopover'
import "react-awesome-lightbox/build/style.css"
import "./TaskList.css"
import moment from "moment"
import { renderCustomColumn } from "./TaskComponents/ForeignKeyComponents"
import { getCustomColumnIcon } from "./columnTypeMeta"
import { TASK_TABLE_HEADERS as taskTableHeaders } from '../Constants'

function TaskList({ searchTerm, loadMoreEntries, allTasks, board, statuses, orderBy, updateOrderBy, ...props }) {
    const [searchParams] = useSearchParams()
    const selectedTaskId = searchParams.get('task_id')
    const { boardId } = useParams()
    const [loading, setLoading] = useState(false)
    const [openModal, setOpenModal] = useState("")
    const [selectedTasks, setSelectedTasks] = useState([])

    // columnConfig is merged (standard + custom) by TaskManagementContainer.
    // When present, it drives column visibility and order.
    // positionIsFixed columns (title) always render regardless of visible flag.
    // Custom columns without columnNode are stale/deleted — filtered out.
    let taskHeaders
    if (props.columnConfig && props.columnConfig.length > 0) {
        taskHeaders = props.columnConfig
            .filter(col => col.visible || col.positionIsFixed)
            .map(col => {
                if (col.isCustom || col.id.startsWith('custom_column_')) {
                    return col.columnNode
                        ? { title: col.name, id: col.id, columnNode: col.columnNode, isCustom: true }
                        : null
                }
                const def = taskTableHeaders.find(h => h.id === col.id)
                return def ? { title: def.name, id: def.id, sortable: def.sortable } : null
            })
            .filter(Boolean)
    } else {
        // Fallback: no preferences yet, use taskTableHeaders + board custom columns
        taskHeaders = taskTableHeaders.map(h => ({ title: h.name, id: h.id, sortable: h.sortable }))
        ;(board?.columnSet?.edges || [])
            .filter(col => !col.node.isArchived)
            .forEach(col => taskHeaders.push({
                title: col.node.title,
                id: `custom_column_${col.node.pk}`,
                columnNode: col.node,
                isCustom: true,
            }))
    }


    const { data: hasPermissions, loading: hasPermissionsLoading } = useQuery(HasPermissionsQuery, {
        variables: {
            userPermissions: ["add_column", "change_taskrabbit"]
        }
    })

    const updateSelected = (value) => {
        let taskSelection = selectedTasks
        if (taskSelection.includes(value)) {
            taskSelection = taskSelection.filter(item => item !== value)
        } else {
            taskSelection.push(value)
        }
        setSelectedTasks([...taskSelection])
    }

    const toggleSelectAllClick = () => {
        if (allTasks?.edges && haveSameValues(selectedTasks, allTasks?.edges.map(driver => driver.node.id))) {
            setSelectedTasks([])
        } else {
            setSelectedTasks([...allTasks?.edges.map(task => task.node.id)])
        }
    }

    const haveSameValues = (arr1, arr2) => {
        if (arr1.length !== arr2.length) {
            return false
        }
        const sortedArr1 = arr1.slice().sort()
        const sortedArr2 = arr2.slice().sort()
        for (let i = 0; i < sortedArr1.length; i++) {
            if (sortedArr1[i] !== sortedArr2[i]) {
                return false
            }
        }
        return true
    }

    const getTaskStatusClass = (status) => {
        if (status === "DONE") return "done"
        if (status === "IN PROGRESS" || status === "IN_PROGRESS") return "in-progress"
        if (status === "INCOMPLETE") return "incomplete"
        if (status === "CANCELED") return "canceled"
        return "open"
    }

    const getDueDateConfig = (dueDate) => {
        if (!dueDate) return { label: "", isPastDue: false, iconClassName: "" }

        let today = moment().startOf("day")
        let due = moment(dueDate).startOf("day")
        let dayDiff = due.diff(today, "days")

        if (dayDiff === 0) {
            return { label: "Today", isPastDue: false, iconClassName: "fa fa-circle" }
        }
        if (dayDiff > 0) {
            return {
                label: `${dayDiff} Day${dayDiff === 1 ? "" : "s"} Left`,
                isPastDue: false,
                iconClassName: dayDiff < 7 ? "fa fa-circle-o text-muted" : "fa fa fa-clock-o text-muted",
            }
        }
        if (dayDiff === -1) {
            return { label: "Due Yesterday", isPastDue: true, iconClassName: "fa fa-exclamation-circle text-danger" }
        }

        let overdueDays = Math.abs(dayDiff)
        return {
            label: `${overdueDays} Day${overdueDays === 1 ? "" : "s"} Overdue`,
            isPastDue: true,
            iconClassName: "fa fa-exclamation-circle text-danger",
        }
    }

    return (
        <div className="TaskList">
            {selectedTaskId && <TaskDetailsSidePanel />}
            <PrimaryListingContainer listLoading={loading || props.loading} totalFetchedRows={allTasks && allTasks.edges.length} pageInfo={allTasks && allTasks.pageInfo} loadMoreEntries={allTasks && allTasks.pageInfo && allTasks.pageInfo.hasNextPage ? loadMoreEntries : null} refetchListQuery={props.refetchTasksQuery}>
                <Table responsive bordered>
                    <thead>
                        <tr className="task-column-header text-center">
                            <th onClick={toggleSelectAllClick} className="select-all-checkbox"> {allTasks?.edges && haveSameValues(selectedTasks, allTasks?.edges.map(task => task.node?.id)) ? <i className="fa fa-check-square-o" aria-hidden="true" /> : <i className="fa fa-square-o" aria-hidden="true" />}</th>
                            {taskHeaders.map((header) => {
                                return (
                                    <th key={header.id} className="task-column-header-item">
                                        {header.sortable ? (
                                            orderBy.some(order => order.includes(header.id)) ? (
                                                <i className={`fa ${orderBy.includes(header.id) ? "fa-sort-asc" : "fa-sort-desc"}`} onClick={() => updateOrderBy(header.id)} />
                                            ) : (
                                                <i className="fa fa-sort" onClick={() => updateOrderBy(header.id)} />
                                            )
                                        ) : null}

                                        {header.isCustom && (
                                            <span className={`custom-column-header-badge ${header.columnNode?.columnType === "MIRROR" ? "mirror-column-header-badge" : ""}`} title={header.columnNode?.columnType}>
                                                <i className={`fa ${getCustomColumnIcon(header.columnNode)}`} aria-hidden="true" />
                                            </span>
                                        )}
                                        &nbsp;&nbsp;{header.title}
                                        {header.isCustom && (
                                            <RenderEditColumnPopover header={header} refetchBoardQuery={props.refetchBoardQuery} refetchTasksQuery={props.refetchTasksQuery} />
                                        )}


                                    </th>
                                )
                            })}

                            <th>
                                {boardId && hasPermissions?.hasPermissions?.includes("add_column") &&
                                    <RenderAddCustomColumn refetchQuery={() => { props.refetchBoardQuery(); props.refetchTasksQuery() }} />
                                }
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {allTasks?.edges?.length > 0 ? allTasks.edges.filter(task => task?.node).map((task, index) => (
                            <tr key={index} className={`task-row ${selectedTaskId === task.node?.id && "active-row"}`} >
                                <td scope="row" className="listing-checkbox-wrapper">
                                    {!selectedTasks.includes(task?.node?.id) && <span className="listing-counter">{index + 1}</span>}
                                    <span className="listing-checkbox" onClick={() => updateSelected(task?.node?.id)}>
                                        {selectedTasks.includes(task?.node?.id) ? <i className="fa fa-check-square-o" aria-hidden="true" /> : <i className="fa fa-square-o unchecked" aria-hidden="true" />}
                                    </span>
                                </td>
                                {taskHeaders.map((header, headerIndex) => (
                                    <Fragment key={header.id}>
                                        {header.id === "title" && (
                                            <td key={headerIndex} className="task-cell title">
                                                <span className="task-cell-content">
                                                    <span className="task-title-text">{task.node.title}</span>
                                                    {task.node.isAutomated && (
                                                        <i className="fa fa-cogs task-automated-icon" aria-hidden="true" title="Automated task" />
                                                    )}
                                                    {!task.node.isAutomated && (
                                                        <i id={`titleEdit${task.node.pk}`} title="Edit Task Title" onClick={() => setOpenModal(`titleEdit${task.node.pk}`)} className={"fa fa-pencil-square-o task-edit-icon"} />
                                                    )}
                                                    <Link to={`./?task_id=${task.node.id}`} className="task-expand-icon">
                                                        <i class="fa fa-expand" aria-hidden="true"></i>
                                                    </Link>
                                                </span>
                                                {!task.node.isAutomated && openModal === `titleEdit${task.node.pk}` && <EditTaskPopOver isOpen={openModal === `titleEdit${task.node.pk}`} task={task.node} closeModal={() => setOpenModal("")} target={`titleEdit${task.node.pk}`} title="Edit Task Title" name="title" value={task.node.title} type="string" maxLength={255} refetchTasksQuery={props.refetchTasksQuery} />}
                                            </td>
                                        )}
                                        {header.id === "updates" && (
                                            <td key={headerIndex} className="task-cell text-center">
                                                <span className="task-cell-content">
                                                    <Link to={`./?task_id=${task.node.id}`} style={task.node.commentsCount > 0 ? { color: `#007bff` } : { color: `inherit` }} className="text-decoration-none">
                                                        <i className="fa fa-comments" aria-hidden="true"></i>&nbsp;&nbsp;
                                                        {task.node.commentsCount}
                                                    </Link>
                                                </span>
                                            </td>
                                        )}
                                        {header.id === "status" && (
                                            <td id={`statusTrigger${task.node.pk}`} className={`task-cell status-cell ${getTaskStatusClass(task.node.status)}`} onClick={() => !task.node.isAutomated || !["DONE", "CANCELED"].includes(task.node.status) ? setOpenModal(openModal === `statusEdit${task.node.pk}` ? "" : `statusEdit${task.node.pk}`) : null}>
                                                {toTitleCase(task.node.status)}
                                                {openModal === `statusEdit${task.node.pk}` && (
                                                    <EditTaskPopOver
                                                        task={task.node}
                                                        isOpen={openModal === `statusEdit${task.node.pk}`}
                                                        closeModal={() => setOpenModal("")}
                                                        target={`statusTrigger${task.node.pk}`}
                                                        title="Edit Status"
                                                        name="status"
                                                        value={task.node.status}
                                                        type="string"
                                                        refetchTasksQuery={props.refetchTasksQuery}
                                                    />
                                                )}
                                            </td>
                                        )}
                                        {header.id === "assignedTo" && (
                                            <td key={headerIndex} className="task-cell">
                                                <span className="task-cell-content">
                                                    {task.node.assignedTo && task.node.assignedTo.edges && task.node.assignedTo.edges.length > 0 && task.node.assignedTo.edges.slice(0, 2).map((rep, index) => (
                                                        <RepAvatar key={index} rep={rep.node} />
                                                    ))}
                                                    {task?.node?.assignedTo?.edges?.length > 2 && <Badge id={`AssignedToPopover${task.node.pk}`} color="secondary" size="md" pill={true} className="mr-1" onClick={() => { openModal === `AssignedToPopover${task.node.pk}` ? setOpenModal("") : setOpenModal(`AssignedToPopover${task.node.pk}`) }}>
                                                        +{task.node.assignedTo.edges.length - 2}
                                                    </Badge>}

                                                    <i id={`assignedToEdit${task.node.pk}`} title="Edit Assigned To" onClick={() => setOpenModal(`assignedToEdit${task.node.pk}`)} className={"fa fa-pencil-square-o task-edit-icon"} />
                                                    {openModal === `AssignedToPopover${task.node.pk}` && <Popover trigger="legacy" placement="bottom" isOpen={openModal === `AssignedToPopover${task.node.pk}`} target={`AssignedToPopover${task.node.pk}`} toggle={() => setOpenModal("")}>
                                                        {/* <PopoverHeader className="text-center">Assigned To</PopoverHeader> */}
                                                        <PopoverBody>
                                                            {task?.node?.assignedTo?.edges?.map((rep, index) => (
                                                                <>
                                                                    <RepAvatar key={index} rep={rep.node} keepOpen />
                                                                    <br />
                                                                </>
                                                            ))}
                                                        </PopoverBody>
                                                    </Popover>}

                                                    {openModal === `assignedToEdit${task.node.pk}` && <EditTaskPopOver isOpen={openModal === `assignedToEdit${task.node.pk}`} task={task.node} closeModal={() => setOpenModal("")} target={`assignedToEdit${task.node.pk}`} title="Edit Assigned To" name="assignedToIds" value={task.node.assignedTo && task.node.assignedTo.edges && task.node.assignedTo.edges.length > 0 ? task.node.assignedTo.edges.map(rep => rep.node) : []} type="string" refetchTasksQuery={props.refetchTasksQuery} />}
                                                </span>
                                            </td >
                                        )}
                                        {header.id === "dueDate" && (
                                            <td key={headerIndex} className="task-cell">
                                                {(() => {
                                                    let { label, isPastDue, iconClassName } = getDueDateConfig(task.node.dueDate)
                                                    let dueDateInfoId = `dueDateInfo${task.node.pk}`
                                                    return (
                                                        <span className={`task-cell-content ${isPastDue ? "text-danger" : ""}`}>
                                                            {label && (
                                                                <>
                                                                    <i className={iconClassName} id={dueDateInfoId} style={{ fontSize: 15 }} />
                                                                    <UncontrolledTooltip placement="top" target={dueDateInfoId}>
                                                                        {label}
                                                                    </UncontrolledTooltip>
                                                                </>
                                                            )}
                                                            &nbsp;&nbsp;{task.node.dueDate}
                                                            <i id={`dueDateEdit${task.node.pk}`} title="Edit Due Date" onClick={() => setOpenModal(`dueDateEdit${task.node.pk}`)} className={"fa fa-pencil-square-o task-edit-icon"} />
                                                        </span>
                                                    )
                                                })()}
                                                {openModal === `dueDateEdit${task.node.pk}` &&
                                                    <EditTaskPopOver task={task.node} isOpen={openModal === `dueDateEdit${task.node.pk}`} closeModal={() => setOpenModal("")} target={`dueDateEdit${task.node.pk}`} title="Edit Due Date" name="dueDate" value={task.node.dueDate} type="date" refetchTasksQuery={props.refetchTasksQuery} />
                                                }
                                            </td>
                                        )}
                                        {header.id === "creationLog" && (
                                            <td key={headerIndex} className="task-cell">
                                                <span className="task-cell-content" id={`creationLog${task.node.pk}`}>
                                                    <RepAvatar rep={task.node.createdBy} keepClose />&nbsp;&nbsp;
                                                    {task.node.dateAdded ? <DatetimeRenderer datetime={task.node.dateAdded} /> : ""}
                                                </span>
                                                <UncontrolledTooltip placement="top" target={`creationLog${task.node.pk}`}>
                                                    Created by {task.node.createdBy ? task.node.createdBy.firstName ? (task.node.createdBy.firstName + " " + task.node.createdBy.lastName) : task.node.createdBy.username : "VGM"} on {task.node.dateAdded ? <DatetimeRenderer datetime={task.node.dateAdded} /> : ""}
                                                </UncontrolledTooltip>

                                            </td>
                                        )}
                                        {header.id === "board" && (
                                            <td key={headerIndex} className="task-cell">
                                                <span className="task-cell-content">
                                                    <Link to={`/boards/${task.node.board.id}/tasks/`}>{task.node.board.title}</Link>
                                                </span>
                                            </td>
                                        )}
                                        {header.id.startsWith('custom_column_') && (
                                            <td key={headerIndex} className={`task-cell ${header.columnNode?.columnType === "MIRROR" ? "mirror-column-cell" : ""}`}>
                                                <span className="task-cell-content">
                                                    {task?.node?.taskcolumnvalueSet?.edges?.length > 0 && renderCustomColumn(task.node.taskcolumnvalueSet.edges.find(value => `custom_column_${value.node.column.pk}` === header.id)?.node)}
                                                    {!task.node.isAutomated && header.columnNode?.columnType !== "MIRROR" && (
                                                        <>
                                                            <i id={`${header.id}Edit${task.node.pk}`} title="Edit Custom Column" onClick={() => setOpenModal(`${header.id}Edit${task.node.pk}`)} className={"fa fa-pencil-square-o task-edit-icon"} />
                                                            {openModal === `${header.id}Edit${task.node.pk}` && <EditTaskPopOver task={task.node} isOpen={openModal === `${header.id}Edit${task.node.pk}`} closeModal={() => setOpenModal("")} target={`${header.id}Edit${task.node.pk}`} title={`Edit ${header.title} Column`} name={`${header.id}Value`} value={task.node.taskcolumnvalueSet.edges.find(value => `custom_column_${value.node.column.pk}` === header.id)?.node.grapheneId || task.node.taskcolumnvalueSet.edges.find(value => `custom_column_${value.node.column.pk}` === header.id)?.node.value} columnId={header.columnNode.id} refetchTasksQuery={props.refetchTasksQuery} />}
                                                        </>
                                                    )}
                                                </span>

                                            </td>
                                        )}
                                    </Fragment>
                                ))}
                                <td className="text-center">
                                    {hasPermissions?.hasPermissions?.includes("change_taskrabbit") && !task.node.isAutomated &&
                                        <TaskArchievePopover taskId={task?.node?.id} />
                                    }
                                </td>
                            </tr>
                        )) : !loading && <tr>
                            <td colSpan={taskHeaders.length + 1} className="text-center">No tasks found</td>
                        </tr>
                        }
                    </tbody>
                </Table>
            </PrimaryListingContainer>
        </div>
    )
}

export default graphql(AllTasksQuery, {
    options: ({ boardId, boardIds, searchTerm, statuses, orderBy, assignedToIds, isArchived, incidentId, dueDateGte, dueDateLte, dueDateIsNull }) => ({
        variables: {
            boardIds: boardIds ? boardIds : boardId ? [boardId] : null,
            incidentId,
            searchTerm,
            statuses,
            orderBy,
            assignedToIds,
            isArchived,
            dueDateGte,
            dueDateLte,
            dueDateIsNull,
            first: 30,
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'cache-and-network',
    }),
    props: ({ data: { loading, allTasks, fetchMore, searchTerm, variables, refetch } }) => ({
        loading,
        allTasks,
        variables,
        refetch,
        refetchTasksQuery: () => {
            // Custom refetch function considering pagination
            let currentLength = allTasks && allTasks.edges ? allTasks.edges.length : 30
            return fetchMore({
                query: AllTasksQuery,
                variables: {
                    ...variables,
                    first: currentLength,
                },
                updateQuery: (previousResult, { fetchMoreResult }) => {
                    const newEdges = fetchMoreResult.allTasks.edges
                    const pageInfo = fetchMoreResult.allTasks.pageInfo
                    return {
                        allTasks: {
                            edges: [...newEdges],
                            pageInfo,
                            searchTerm: previousResult.searchTerm,
                            __typename: previousResult.allTasks.__typename,
                        },
                    }
                },
            })
        },
        loadMoreEntries: () => {
            return fetchMore({
                query: AllTasksQuery,
                variables: {
                    after: allTasks.pageInfo.endCursor,
                    ...variables,
                },
                updateQuery: (previousResult, { fetchMoreResult }) => {
                    const newEdges = fetchMoreResult.allTasks.edges
                    const pageInfo = fetchMoreResult.allTasks.pageInfo

                    return {
                        allTasks: {
                            edges: [...previousResult.allTasks.edges, ...newEdges],
                            pageInfo,
                            searchTerm: previousResult.searchTerm,
                            __typename: previousResult.allTasks.__typename,
                        },
                    }
                },
            })
        },
    }),
})(withApolloClient(TaskList))
